/*
 * RetechVN Pro – UI components.
 * Search, localization, video, comparison slider, promo popup, section group,
 * dynamic dates, modal, clickable discount and the country blocker.
 * Depends on assets/core.js (loaded first).
 */

/* ---------- Country blocker (Theme settings > Security) ---------- */

(() => {
  const script = document.currentScript;
  if (!script || window.Shopify?.designMode) return;

  const mode = script.dataset.countryListFunction === 'whitelist' ? 'whitelist' : 'block';
  const countries = (script.dataset.countryList || '')
    .split(',')
    .map((code) => code.trim().toUpperCase())
    .filter(Boolean);
  const message = script.dataset.countryListError || '';
  if (countries.length === 0) return;

  const storageKey = 'theme:detected-country';

  const readCachedCountry = () => {
    try {
      return sessionStorage.getItem(storageKey);
    } catch (error) {
      return null;
    }
  };

  const cacheCountry = (code) => {
    try {
      sessionStorage.setItem(storageKey, code);
    } catch (error) {
      /* storage unavailable (private mode) – ignore */
    }
  };

  const detectCountry = async () => {
    const cached = readCachedCountry();
    if (cached) return cached;
    const response = await fetch('/browsing_context_suggestions.json', { headers: { Accept: 'application/json' } });
    if (!response.ok) return null;
    const data = await response.json();
    const code = data?.detected_values?.country?.handle;
    if (code) cacheCountry(code.toUpperCase());
    return code ? code.toUpperCase() : null;
  };

  const isBlocked = (code) => {
    const listed = countries.includes(code);
    return mode === 'whitelist' ? !listed : listed;
  };

  const showBlockedPage = () => {
    const render = () => {
      document.body.innerHTML = '';
      const wrapper = document.createElement('div');
      wrapper.className = 'country-blocker page-width';
      wrapper.style.cssText = 'min-height:100vh;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;';
      wrapper.innerHTML = message;
      document.body.appendChild(wrapper);
    };
    if (document.body) render();
    else document.addEventListener('DOMContentLoaded', render, { once: true });
  };

  detectCountry()
    .then((code) => {
      if (code && isBlocked(code)) showBlockedPage();
    })
    .catch((error) => console.error('Country detection failed', error));
})();

/* ---------- Search form (sections/header.liquid) ---------- */

class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');

    if (this.input) {
      this.input.form.addEventListener('reset', this.onFormReset.bind(this));
      this.input.addEventListener(
        'input',
        debounce((event) => this.onChange(event), ON_CHANGE_DEBOUNCE_TIMER)
      );
    }
  }

  toggleResetButton() {
    if (!this.resetButton) return;
    const isHidden = this.resetButton.classList.contains('hidden');
    if (this.input.value.length > 0 && isHidden) {
      this.resetButton.classList.remove('hidden');
    } else if (this.input.value.length === 0 && !isHidden) {
      this.resetButton.classList.add('hidden');
    }
  }

  onChange() {
    this.toggleResetButton();
  }

  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a');
  }

  onFormReset(event) {
    // Let the browser reset happen only when no result is highlighted (Enter on a result).
    event.preventDefault();
    if (this.shouldResetForm()) {
      this.input.value = '';
      this.input.focus();
      this.toggleResetButton();
    }
  }
}

if (!customElements.get('search-form')) customElements.define('search-form', SearchForm);

/* ---------- Predictive search (sections/predictive-search.liquid) ---------- */

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.resultsContainer = this.querySelector('[data-predictive-search]');
    this.statusElement = this.querySelector('.predictive-search-status');
    this.loadingText = this.dataset.loadingText || '';
    this.isOpen = false;
    this.searchTerm = '';
    this.abortController = new AbortController();

    if (!this.input || !this.resultsContainer) return;

    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();

    // Drop stale results unless the user is just extending the previous term.
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }
    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();
    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Keep the caret in place while moving through results.
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') event.preventDefault();
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const textElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentText = textElement?.innerText;
    if (!currentText || !previousTerm) return;

    // Only swap the term when it appears exactly once, to avoid mangling translated text.
    if (currentText.split(previousTerm).length - 1 !== 1) return;
    textElement.innerText = currentText.replace(previousTerm, newTerm);
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Only options that are actually visible (mobile/desktop page lists are duplicated).
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    if (allVisibleElements.length === 0) return;

    if (moveUp && !selectedElement) return;

    let activeElementIndex = 0;
    const selectedElementIndex = allVisibleElements.indexOf(selectedElement);
    if (this.statusElement) this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];
    activeElement.setAttribute('aria-selected', 'true');
    if (selectedElement) selectedElement.setAttribute('aria-selected', 'false');

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/\s+/g, '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const url = `${window.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`;

    fetch(url, { signal: this.abortController.signal })
      .then((response) => {
        if (!response.ok) {
          this.close();
          throw new Error(`Predictive search failed: ${response.status}`);
        }
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search')?.innerHTML;
        if (resultsMarkup === undefined) return;

        // Share the cache with the other instances (desktop/mobile header, search page).
        document.querySelectorAll('predictive-search').forEach((instance) => {
          if (instance.cachedResults) instance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        this.close();
        console.error(error);
      });
  }

  setLiveRegionLoadingState() {
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', 'true');
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;
    setTimeout(() => this.statusElement.setAttribute('aria-hidden', 'true'), 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.resultsContainer.innerHTML = resultsMarkup;
    this.setAttribute('results', 'true');
    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const countElement = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (countElement) this.setLiveRegionText(countElement.textContent.trim());
  }

  getResultsMaxHeight() {
    const header = document.querySelector('.section-header') || document.querySelector('.header-wrapper');
    const headerBottom = header ? header.getBoundingClientRect().bottom : 0;
    this.resultsMaxHeight = window.innerHeight - headerBottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.resultsContainer.style.maxHeight = `${this.resultsMaxHeight || this.getResultsMaxHeight()}px`;
    this.setAttribute('open', 'true');
    this.input.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    this.querySelector('[aria-selected="true"]')?.setAttribute('aria-selected', 'false');
    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', 'false');
    this.resultsMaxHeight = false;
    this.resultsContainer.removeAttribute('style');
  }
}

if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);

/* ---------- Localization form (sections/footer.liquid) ---------- */

class ThemeLocalizationForm extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    this.input = this.querySelector('input[name="locale_code"], input[name="country_code"]');
    this.button = this.querySelector('.disclosure__button');
    this.panel = this.querySelector('.disclosure__list-wrapper');
    if (!this.button || !this.panel) return;

    this.onDocumentClick = this.onDocumentClick.bind(this);

    this.button.addEventListener('click', this.togglePanel.bind(this));
    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.querySelectorAll('.disclosure__link').forEach((link) =>
      link.addEventListener('click', this.onItemClick.bind(this))
    );
  }

  isOpen() {
    return !this.panel.hasAttribute('hidden');
  }

  openPanel() {
    this.panel.removeAttribute('hidden');
    this.button.setAttribute('aria-expanded', 'true');
    document.addEventListener('click', this.onDocumentClick);
  }

  hidePanel() {
    this.panel.setAttribute('hidden', '');
    this.button.setAttribute('aria-expanded', 'false');
    document.removeEventListener('click', this.onDocumentClick);
  }

  togglePanel() {
    this.button.focus();
    if (this.isOpen()) this.hidePanel();
    else this.openPanel();
  }

  onDocumentClick(event) {
    if (!this.contains(event.target)) this.hidePanel();
  }

  onFocusOut(event) {
    // relatedTarget is null when clicking non-focusable areas; the document click handler covers that.
    if (event.relatedTarget && !this.contains(event.relatedTarget)) this.hidePanel();
  }

  onKeyUp(event) {
    if (event.code?.toUpperCase() !== 'ESCAPE' || !this.isOpen()) return;
    this.hidePanel();
    this.button.focus();
  }

  onItemClick(event) {
    event.preventDefault();
    if (!this.input || !this.form) return;
    this.input.value = event.currentTarget.dataset.value;
    this.form.submit();
  }
}

if (!customElements.get('localization-form')) customElements.define('localization-form', ThemeLocalizationForm);

/* ---------- Internal video (snippets/video-player.liquid and others) ---------- */

class InternalVideo extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.video = this.querySelector('video');
    if (!this.video) return;
    this.initialized = true;

    this.playButton = this.querySelector('.internal-video__play');
    this.soundButton = this.querySelector('.internal-video__sound-btn');
    this.timeline = this.querySelector('.internal-video__timeline');
    this.autoplay = this.dataset.autoplay === 'true';
    this.pausedByUser = false;

    this.bindVideoEvents();
    this.playButton?.addEventListener('click', this.onPlayButtonClick.bind(this));
    this.soundButton?.addEventListener('click', this.onSoundButtonClick.bind(this));
    if (this.timeline) this.bindTimeline();

    this.syncState();
    if (this.autoplay) this.observeVisibility();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.observer = null;
    this.initialized = false;
  }

  bindVideoEvents() {
    const video = this.video;
    video.addEventListener('play', () => this.classList.add('internal-video--playing'));
    video.addEventListener('playing', () => this.classList.remove('internal-video--loading'));
    video.addEventListener('pause', () => this.classList.remove('internal-video--playing'));
    video.addEventListener('ended', () => {
      if (!video.loop) this.classList.remove('internal-video--playing');
    });
    video.addEventListener('waiting', () => this.classList.add('internal-video--loading'));
    video.addEventListener('canplay', () => this.classList.remove('internal-video--loading'));
    video.addEventListener('volumechange', () => this.syncState());
    video.addEventListener('timeupdate', () => this.updateTimeline());
  }

  syncState() {
    this.classList.toggle('internal-video--muted', this.video.muted);
  }

  /** Swap `source[data-src]` to `src` the first time the video is needed. */
  loadSources() {
    const lazySources = this.video.querySelectorAll('source[data-src]');
    if (lazySources.length === 0) return;
    lazySources.forEach((source) => {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
    });
    this.video.load();
  }

  observeVisibility() {
    if (!('IntersectionObserver' in window)) {
      this.play();
      return;
    }
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (!this.pausedByUser) this.play();
          } else if (!this.video.paused) {
            this.video.pause();
          }
        });
      },
      { rootMargin: '100px 0px' }
    );
    this.observer.observe(this);
  }

  play() {
    this.loadSources();
    const promise = this.video.play();
    if (promise?.catch) {
      promise.catch(() => {
        // Autoplay blocked or source failed – show the play button again.
        this.classList.remove('internal-video--playing', 'internal-video--loading');
      });
    }
  }

  pause() {
    this.video.pause();
  }

  onPlayButtonClick(event) {
    event.preventDefault();
    if (this.video.paused) {
      this.pausedByUser = false;
      this.pauseOtherVideos();
      this.play();
    } else {
      this.pausedByUser = true;
      this.pause();
    }
  }

  onSoundButtonClick(event) {
    event.preventDefault();
    event.stopPropagation();
    this.video.muted = !this.video.muted;
    if (!this.video.muted) {
      this.pauseOtherVideos();
      if (this.video.paused) {
        this.pausedByUser = false;
        this.play();
      }
    }
    this.syncState();
  }

  /** Pause other click-to-play videos so only one plays with sound. */
  pauseOtherVideos() {
    document.querySelectorAll('internal-video').forEach((element) => {
      if (element === this || !element.video || element.video.paused) return;
      if (element.autoplay && element.video.muted) return;
      element.pausedByUser = true;
      element.video.pause();
    });
  }

  updateTimeline() {
    if (!this.timeline || !this.video.duration) return;
    const percent = (this.video.currentTime / this.video.duration) * 100;
    this.timeline.style.setProperty('--completed', `${percent}%`);
  }

  bindTimeline() {
    const isRtl = document.documentElement.dir === 'rtl';

    const seek = (clientX) => {
      if (!this.video.duration) return;
      const rect = this.timeline.getBoundingClientRect();
      let ratio = (clientX - rect.left) / rect.width;
      if (isRtl) ratio = 1 - ratio;
      ratio = Math.min(Math.max(ratio, 0), 1);
      this.video.currentTime = ratio * this.video.duration;
      this.updateTimeline();
    };

    this.timeline.addEventListener('pointerdown', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.isSeeking = true;
      this.timeline.setPointerCapture?.(event.pointerId);
      seek(event.clientX);
    });
    this.timeline.addEventListener('pointermove', (event) => {
      if (this.isSeeking) seek(event.clientX);
    });
    const stopSeeking = () => {
      this.isSeeking = false;
    };
    this.timeline.addEventListener('pointerup', stopSeeking);
    this.timeline.addEventListener('pointercancel', stopSeeking);
    this.timeline.addEventListener('click', (event) => event.stopPropagation());
  }
}

if (!customElements.get('internal-video')) customElements.define('internal-video', InternalVideo);

/* ---------- Before/after comparison slider (sections/comparison-slider.liquid) ---------- */

class ComparisonSlider extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.input = this.querySelector('.comparison-slider__input');
    this.overlay = this.querySelector('.comparison-slider__overlay');
    this.line = this.querySelector('.comparison-slider__line');
    if (!this.input) return;
    this.initialized = true;

    this.input.addEventListener('input', () => this.update());
    this.input.addEventListener('change', () => this.update());
    this.update();
  }

  update() {
    const position = `${Number(this.input.value)}%`;
    this.style.setProperty('--position', position);
    if (this.overlay) this.overlay.style.width = position;
    if (this.line) {
      this.line.style.setProperty('--position', position);
      this.line.style.left = position;
    }
  }
}

if (!customElements.get('comparison-slider')) customElements.define('comparison-slider', ComparisonSlider);

/* ---------- Promo popup (sections/promo-popup.liquid) ---------- */

class PromoPopup extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    const host = window.location.host;
    this.seenKey = `promo-popup-seen-${host}`;
    this.subscribedKey = `promo-bar-subscribed-${host}`;

    this.testMode = this.dataset.testMode === 'true';
    this.delaySeconds = Number(this.dataset.delaySeconds) || 0;
    this.delayDays = Number(this.dataset.delayDays) || 0;
    this.frequencyEnabled = this.dataset.frequencyEnabled === 'true';
    this.initialPopup = this.dataset.initialPopup === 'success' ? 'success' : 'content';
    this.displayTimer = this.dataset.displayTimer === 'true';
    this.timerDuration = Number(this.dataset.timerDuration) || 0;

    this.popups = {
      content: {
        modal: this.querySelector('.sign-up-popup-modal'),
        overlay: this.querySelector('.sign-up-popup-overlay'),
      },
      success: {
        modal: this.querySelector('.success-popup-modal'),
        overlay: this.querySelector('.success-popup-overlay'),
      },
    };

    this.onKeyUp = this.onKeyUp.bind(this);
    this.querySelectorAll('.promp-popup__close-btn').forEach((button) =>
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.close();
      })
    );
    this.querySelector('.popup-modal__copy-btn')?.addEventListener('click', this.copyCode.bind(this));
    this.querySelector('.popup-modal__email-form form')?.addEventListener('submit', () => this.markSeen());
    document.addEventListener('keyup', this.onKeyUp);

    this.bindEditorEvents();

    // Markup already activates the popup in test mode; make sure the timer runs.
    const activeName = this.getActivePopupName();
    if (activeName) {
      this.onOpened(activeName);
      return;
    }

    if (this.testMode) {
      this.open(this.initialPopup);
      return;
    }

    // In the editor only test mode or selecting the section shows the popup.
    if (window.Shopify?.designMode) return;
    // After a newsletter post the success popup is handled by the inline script in the section.
    if (/[?&]customer_posted=true/.test(window.location.search)) return;
    if (!this.shouldShow()) return;

    this.delayTimeout = setTimeout(() => this.open(this.initialPopup), this.delaySeconds * 1000);
  }

  disconnectedCallback() {
    clearTimeout(this.delayTimeout);
    this.stopTimer();
    document.removeEventListener('keyup', this.onKeyUp);
    this.initialized = false;
  }

  bindEditorEvents() {
    if (!window.Shopify?.designMode || this.editorEventsBound) return;
    this.editorEventsBound = true;
    const section = this.closest('.shopify-section');
    document.addEventListener('shopify:section:select', (event) => {
      if (section && event.target === section) this.open(this.initialPopup);
    });
    document.addEventListener('shopify:section:deselect', (event) => {
      if (section && event.target === section && !this.testMode) this.close();
    });
  }

  shouldShow() {
    if (this.initialPopup === 'content' && this.readStorage(localStorage, this.subscribedKey)) return false;

    if (this.frequencyEnabled) {
      const seenAt = Number(this.readStorage(localStorage, this.seenKey));
      if (!seenAt) return true;
      return Date.now() - seenAt >= this.delayDays * 24 * 60 * 60 * 1000;
    }
    return !this.readStorage(sessionStorage, this.seenKey);
  }

  markSeen() {
    if (this.testMode) return;
    const storage = this.frequencyEnabled ? localStorage : sessionStorage;
    try {
      storage.setItem(this.seenKey, String(Date.now()));
    } catch (error) {
      /* storage unavailable – popup may show again */
    }
  }

  readStorage(storage, key) {
    try {
      return storage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  getActivePopupName() {
    return Object.keys(this.popups).find((name) =>
      this.popups[name].modal?.classList.contains('popup-modal--active')
    );
  }

  open(name) {
    // Fall back to whichever popup exists if the requested one is disabled.
    if (!this.popups[name]?.modal) name = Object.keys(this.popups).find((key) => this.popups[key].modal);
    const target = this.popups[name];
    if (!target?.modal) return;

    this.close(false);
    target.modal.classList.add('popup-modal--active');
    target.overlay?.classList.add('popup-overlay--active');
    this.onOpened(name);
    this.markSeen();
  }

  onOpened(name) {
    const modal = this.popups[name]?.modal;
    if (!modal) return;
    this.activeModal = modal;
    if (name === 'content') this.startTimer();
    // Don't steal focus in the editor; it interferes with the settings sidebar.
    if (!window.Shopify?.designMode) trapFocus(modal, modal.querySelector('.popup-modal__close'));
  }

  close(restoreFocus = true) {
    Object.values(this.popups).forEach(({ modal, overlay }) => {
      modal?.classList.remove('popup-modal--active');
      overlay?.classList.remove('popup-overlay--active');
    });
    this.stopTimer();
    if (this.activeModal) {
      this.activeModal = null;
      if (restoreFocus) removeTrapFocus();
    }
  }

  onKeyUp(event) {
    if (event.code?.toUpperCase() === 'ESCAPE' && this.activeModal) this.close();
  }

  startTimer() {
    const timer = this.querySelector('.popup-modal__timer');
    if (!this.displayTimer || !timer || this.timerInterval) return;

    const minutesElement = timer.querySelector('.popup-modal__timer__minutes');
    const secondsElement = timer.querySelector('.popup-modal__timer__seconds');
    let remaining = Math.round(this.timerDuration * 60);

    const render = () => {
      const minutes = Math.floor(remaining / 60);
      const seconds = remaining % 60;
      if (minutesElement) minutesElement.textContent = String(minutes).padStart(2, '0');
      if (secondsElement) secondsElement.textContent = String(seconds).padStart(2, '0');
    };

    render();
    this.timerInterval = setInterval(() => {
      remaining = Math.max(remaining - 1, 0);
      render();
      if (remaining === 0) this.stopTimer();
    }, 1000);
  }

  stopTimer() {
    clearInterval(this.timerInterval);
    this.timerInterval = null;
  }

  async copyCode() {
    const input = this.querySelector('.popup-modal__discount-code input');
    const message = this.querySelector('.popup-modal__success-msg');
    if (!input) return;

    if (message && !message.dataset.successMessage) message.dataset.successMessage = message.textContent;

    let copied = false;
    try {
      await navigator.clipboard.writeText(input.value);
      copied = true;
    } catch (error) {
      input.select();
      try {
        copied = document.execCommand('copy');
      } catch (fallbackError) {
        copied = false;
      }
    }

    if (!message) return;
    message.textContent = copied ? message.dataset.successMessage : message.dataset.copyErrorMessage || '';
    message.style.display = 'block';
  }
}

if (!customElements.get('promo-popup')) customElements.define('promo-popup', PromoPopup);

/* ---------- Section group (sections/section-group.liquid) ---------- */

class SectionGroup extends HTMLElement {
  connectedCallback() {
    this.moveSections();

    if (window.Shopify?.designMode && !this.editorListener) {
      this.editorListener = () => this.moveSections();
      document.addEventListener('shopify:section:load', this.editorListener);
      document.addEventListener('shopify:section:reorder', this.editorListener);
    }
  }

  disconnectedCallback() {
    if (!this.editorListener) return;
    document.removeEventListener('shopify:section:load', this.editorListener);
    document.removeEventListener('shopify:section:reorder', this.editorListener);
    this.editorListener = null;
  }

  /** Accepts "template--123__abc", "shopify-section-template--…" or "#shopify-section-template--…". */
  findSection(rawId) {
    const id = (rawId || '').trim().replace(/^#/, '');
    if (!id) return null;
    return document.getElementById(id) || document.getElementById(`shopify-section-${id}`);
  }

  moveSections() {
    const targets = [
      [this.dataset.sectionOneId, '.section-group__section-one-container'],
      [this.dataset.sectionTwoId, '.section-group__section-two-container'],
    ];

    targets.forEach(([id, containerSelector]) => {
      const container = this.querySelector(containerSelector);
      const section = this.findSection(id);
      if (!container || !section || section.contains(this)) return;

      const content = section.querySelector('.content-for-grouping') || section.firstElementChild;
      if (!content || container.contains(content)) return;

      container.replaceChildren(content);
      section.setAttribute('hidden', '');
    });
  }
}

if (!customElements.get('section-group')) customElements.define('section-group', SectionGroup);

/* ---------- Dynamic delivery dates (estimated-shipping / shipping-checkpoints) ---------- */

class DynamicDates extends HTMLElement {
  static DEFAULT_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  static DEFAULT_MONTHS = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  connectedCallback() {
    // Day labels start on Monday, as in the block settings.
    const dayLabels = this.parseList(this.dataset.dayLabels, []);
    const monthLabels = this.parseList(this.dataset.monthLabels, []);
    this.dayLabels = dayLabels.length === 7 ? dayLabels : DynamicDates.DEFAULT_DAYS;
    this.monthLabels = monthLabels.length === 12 ? monthLabels : DynamicDates.DEFAULT_MONTHS;
    this.dateFormat = this.dataset.dateFormat || 'day_mm_dd';
    this.skipDays = this.parseSkipDays(this.dataset.skipDays);

    this.render();
  }

  parseList(value, fallback) {
    const list = (value || '')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
    return list.length ? list : fallback;
  }

  /**
   * Optional `data-skip-days` (e.g. "Saturday, Sunday" or "6,0"): weekdays that don't count
   * towards delivery time. Names are matched against the English names and the day labels.
   */
  parseSkipDays(value) {
    const englishDays = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const skip = new Set();
    this.parseList(value, []).forEach((item) => {
      const lower = item.toLowerCase();
      if (/^\d$/.test(lower)) {
        skip.add(Number(lower) % 7);
        return;
      }
      const englishIndex = englishDays.indexOf(lower);
      if (englishIndex !== -1) {
        skip.add(englishIndex);
        return;
      }
      const labelIndex = this.dayLabels.findIndex((label) => label.toLowerCase() === lower);
      if (labelIndex !== -1) skip.add((labelIndex + 1) % 7);
    });
    // Never skip every day of the week.
    return skip.size >= 7 ? new Set() : skip;
  }

  addDeliveryDays(start, days) {
    if (this.skipDays.size === 0) return addDays(start, days);
    let date = new Date(start);
    let remaining = days;
    while (remaining > 0) {
      date = addDays(date, 1);
      if (!this.skipDays.has(date.getDay())) remaining--;
    }
    return date;
  }

  ordinal(day) {
    const mod100 = day % 100;
    if (mod100 >= 11 && mod100 <= 13) return `${day}th`;
    switch (day % 10) {
      case 1:
        return `${day}st`;
      case 2:
        return `${day}nd`;
      case 3:
        return `${day}rd`;
      default:
        return `${day}th`;
    }
  }

  formatDate(date) {
    const dayName = this.dayLabels[(date.getDay() + 6) % 7];
    const monthName = this.monthLabels[date.getMonth()];
    const day = date.getDate();
    const dd = String(day).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');

    switch (this.dateFormat) {
      case 'day_dd_mm':
        return `${dayName}, ${day}. ${monthName}`;
      case 'mm_dd':
        return `${monthName} ${this.ordinal(day)}`;
      case 'dd_mm':
        return `${day}. ${monthName}`;
      case 'dd_mm_no_dot':
        return `${day} ${monthName}`;
      case 'day_dd_mm_numeric':
        return `${dayName}, ${dd}. ${mm}.`;
      case 'dd_mm_numeric':
        return `${dd}. ${mm}.`;
      case 'day_mm_dd':
      default:
        return `${dayName}, ${monthName} ${this.ordinal(day)}`;
    }
  }

  render() {
    const today = new Date();
    this.querySelectorAll('[data-dynamic-date="true"]').forEach((element) => {
      const template = element.dataset.text;
      if (!template) return;

      const minDays = parseInt(element.dataset.minDays, 10);
      const maxDays = parseInt(element.dataset.maxDays, 10);
      const min = Number.isNaN(minDays) ? 0 : Math.max(minDays, 0);
      const max = Number.isNaN(maxDays) ? min : Math.max(maxDays, min);

      const startDate = this.formatDate(this.addDeliveryDays(today, min));
      const endDate = this.formatDate(this.addDeliveryDays(today, max));

      element.innerHTML = template.split('[start_date]').join(startDate).split('[end_date]').join(endDate);
    });
  }
}

if (!customElements.get('dynamic-dates')) customElements.define('dynamic-dates', DynamicDates);

/* ---------- Generic modal (snippets/sizing-chart.liquid) ---------- */

class ModalV2 extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.modal = this.querySelector('.modal-v2__modal');
    this.openButton = this.querySelector('.modal-v2__open-btn');
    this.onKeyUp = this.onKeyUp.bind(this);

    this.openButton?.addEventListener('click', (event) => {
      event.preventDefault();
      this.open();
    });
    this.querySelectorAll('.modal-v2__close-btn').forEach((button) =>
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.close();
      })
    );

    // The chart can be moved inside a <label for="…">; stop clicks from toggling that input.
    this.addEventListener('click', (event) => {
      if (this.closest('label') && !event.target.closest('a')) event.preventDefault();
    });

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', (event) => {
        if (event.target === this || event.target.contains?.(this)) this.open();
      });
      document.addEventListener('shopify:block:deselect', (event) => {
        if (event.target === this || event.target.contains?.(this)) this.close();
      });
    }
  }

  disconnectedCallback() {
    document.removeEventListener('keyup', this.onKeyUp);
    this.initialized = false;
  }

  isOpen() {
    return this.dataset.open === 'true';
  }

  open() {
    this.dataset.open = 'true';
    this.openButton?.setAttribute('aria-expanded', 'true');
    document.body.classList.add('overflow-hidden');
    document.addEventListener('keyup', this.onKeyUp);
    if (this.modal) {
      if (!this.modal.hasAttribute('tabindex')) this.modal.setAttribute('tabindex', '-1');
      trapFocus(this.modal, this.modal.querySelector('.modal-v2__close-btn') || this.modal);
    }
  }

  close() {
    if (!this.isOpen()) return;
    this.dataset.open = 'false';
    this.openButton?.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('overflow-hidden');
    document.removeEventListener('keyup', this.onKeyUp);
    removeTrapFocus(this.openButton);
  }

  onKeyUp(event) {
    if (event.code?.toUpperCase() === 'ESCAPE') this.close();
  }
}

if (!customElements.get('modal-v2')) customElements.define('modal-v2', ModalV2);

/* ---------- Clickable discount (main-product "clickable_discount" block) ---------- */

class ClickableDiscount extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.code = (this.dataset.code || '').trim();
    this.button = this.querySelector('.clickable-discount__btn');
    if (!this.code || !this.button) return;
    this.initialized = true;

    this.button.addEventListener('click', (event) => {
      event.preventDefault();
      this.apply();
    });
    this.checkApplied();
  }

  setState({ applied, loading, error }) {
    if (applied !== undefined) this.dataset.applied = String(applied);
    if (loading !== undefined) this.dataset.loading = String(loading);
    if (error !== undefined) this.dataset.error = String(error);
    this.button.toggleAttribute('disabled', this.dataset.loading === 'true');
  }

  hasCode(cart) {
    const target = this.code.toLowerCase();
    return (cart?.discount_codes || []).some((discount) => discount.code.toLowerCase() === target);
  }

  async checkApplied() {
    try {
      const cart = await theme.cart.get();
      if (this.hasCode(cart)) this.setState({ applied: true });
    } catch (error) {
      /* cart unavailable – leave button in its default state */
    }
  }

  async apply() {
    if (this.dataset.applied === 'true' || this.dataset.loading === 'true') return;
    this.setState({ loading: true, error: false });

    try {
      const current = await theme.cart.get();
      const codes = [...new Set([...(current.discount_codes || []).map((d) => d.code), this.code])].join(',');

      const response = await fetch(`${window.routes.cart_update_url}.js`, {
        ...fetchConfig(),
        body: JSON.stringify({ discount: codes }),
      });
      if (!response.ok) throw new Error(`Discount request failed: ${response.status}`);
      const cart = await response.json();
      if (!this.hasCode(cart)) throw new Error('Discount code was not accepted');

      this.setState({ applied: true, loading: false });
      theme.cart.refresh();
    } catch (error) {
      console.error(error);
      this.setState({ loading: false, error: true });
    }
  }
}

if (!customElements.get('clickable-discount')) customElements.define('clickable-discount', ClickableDiscount);
